import React, {
  createContext, useState, useContext, ReactNode, useMemo,
} from 'react';
import { StatusMessage } from '../types';

type StatusMessageState = {
  statusMessage?: StatusMessage;
};

type StatusMessageDispatch = {
  setStatusMessage: (message?: StatusMessage) => void;
};

const StatusMessageStateContext = createContext<StatusMessageState | undefined>(undefined);
const StatusMessageDispatchContext = createContext<StatusMessageDispatch | undefined>(undefined);

export const StatusMessageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [ statusMessageState, setStatusMessageState ] = useState<StatusMessage>();

  // Wrapper for setStatusMessage that automatically adds messageInstanceId
  const setStatusMessage = (message?: StatusMessage) => {
    if (message) {
      // Always add a messageInstanceId to ensure the message is displayed
      const enhancedMessage = {
        ...message,
        messageInstanceId: Date.now(),
      };
      setStatusMessageState(enhancedMessage);
    } else {
      setStatusMessageState(undefined);
    }
  };

  // Memoize the state and dispatch values to prevent unnecessary re-renders
  const state = useMemo(() => ({ statusMessage: statusMessageState }), [ statusMessageState ]);
  const dispatch = useMemo(() => ({ setStatusMessage }), []);

  return (
    <StatusMessageDispatchContext.Provider value={dispatch}>
      <StatusMessageStateContext.Provider value={state}>
        {children}
      </StatusMessageStateContext.Provider>
    </StatusMessageDispatchContext.Provider>
  );
};

// Hook for components that only need to read the status message
export const useStatusMessageState = (): StatusMessageState => {
  const context = useContext(StatusMessageStateContext);
  if (context === undefined) {
    throw new Error('useStatusMessageState must be used within a StatusMessageProvider');
  }
  return context;
};

// Hook for components that only need to set the status message
export const useStatusMessageDispatch = (): StatusMessageDispatch => {
  const context = useContext(StatusMessageDispatchContext);
  if (context === undefined) {
    throw new Error('useStatusMessageDispatch must be used within a StatusMessageProvider');
  }
  return context;
};

// Combined hook for convenience when a component needs both
export const useStatusMessage = (): StatusMessageState & StatusMessageDispatch => {
  return {
    ...useStatusMessageState(),
    ...useStatusMessageDispatch(),
  };
};
